import type { PlaybookId } from "@repo/app-types/entities/playbook";
import {
  MultiSelect,
  type MultiSelectProps,
  type MultiSelectSection,
} from "@repo/ui/Select/multiSelect";
import { useEffect, useMemo, type FC } from "react";
import {
  buildPlaybookAndVoiceAgentSections,
  type PlaybookAndVoiceAgentOption,
} from "../hooks/usePlaybookAndVoiceAgentOptions.js";

export interface PlaybookAndVoiceAgentMultiSelectChange {
  /** Selected playbook ids (voice agents included, valued by backing playbook). */
  playbookIds: readonly PlaybookId[];
  /** Set when the change was adding one of `leadingSections`' options. */
  addedLeadingOptionId?: string;
}

export interface PlaybookAndVoiceAgentMultiSelectProps {
  options: readonly PlaybookAndVoiceAgentOption[];
  isLoading: boolean;
  /**
   * True when `options` is the complete list (every backing query succeeded).
   * Gates dropping selected ids that are missing from `options`: a failed
   * fetch must not read as "these playbooks were deleted."
   */
  hasLoadedAllOptions: boolean;
  /**
   * Extra sections of ordinary selectable rows listed above the playbook /
   * voice-agent sections — options, not headers. The signal scope field passes
   * one unlabeled section holding its "All playbooks…" rows. Selecting one of
   * these rows is reported through `addedLeadingOptionId` and clears the
   * pinned selection.
   */
  leadingSections?: readonly MultiSelectSection[];
  selectedPlaybookIds: readonly PlaybookId[];
  /** Selected option from `leadingSections`; mutually exclusive with playbook ids. */
  selectedLeadingOptionId?: string;
  onChange: (change: PlaybookAndVoiceAgentMultiSelectChange) => void;
  size?: MultiSelectProps["size"];
  isDisabled?: boolean;
}

/**
 * Multi-select over the company's playbooks and voice agents, grouped under
 * "Playbook" / "Voice agent" headers. Voice agent options carry the playbook
 * backing the agent. Selected ids that no longer resolve to a playbook
 * (deleted playbooks) are dropped from the selection.
 */
export const PlaybookAndVoiceAgentMultiSelect: FC<
  PlaybookAndVoiceAgentMultiSelectProps
> = (props) => {
  const { onChange } = props;
  const leadingSections = props.leadingSections ?? [];

  const unavailableIds = useMemo(
    () =>
      props.hasLoadedAllOptions && props.selectedLeadingOptionId === undefined
        ? props.selectedPlaybookIds.filter(
            (id) => !props.options.some((option) => option.value === id),
          )
        : [],
    [
      props.hasLoadedAllOptions,
      props.options,
      props.selectedLeadingOptionId,
      props.selectedPlaybookIds,
    ],
  );

  useEffect(() => {
    if (unavailableIds.length === 0) {
      return;
    }

    const availableIds = props.selectedPlaybookIds.filter((id) =>
      props.options.some((option) => option.value === id),
    );
    onChange({ playbookIds: availableIds });
  }, [onChange, props.options, props.selectedPlaybookIds, unavailableIds]);

  const sections: readonly MultiSelectSection[] = [
    ...leadingSections,
    ...buildPlaybookAndVoiceAgentSections(props.options).map((section) => ({
      id: section.id,
      label: section.label,
      options: section.options.map((option) => ({
        id: option.value,
        label: option.label,
      })),
    })),
  ];

  const leadingOptionById = new Map(
    leadingSections.flatMap((section) =>
      section.options.map((option) => [option.id, option]),
    ),
  );

  const selectedIds: readonly string[] =
    props.selectedLeadingOptionId !== undefined
      ? [props.selectedLeadingOptionId]
      : props.selectedPlaybookIds;

  const selectionLabel = (() => {
    if (props.selectedLeadingOptionId !== undefined) {
      return leadingOptionById.get(props.selectedLeadingOptionId)?.label;
    }
    const selectedNames = props.options
      .filter((option) => props.selectedPlaybookIds.includes(option.value))
      .map((option) => option.label);
    if (selectedNames.length === 0) {
      return undefined;
    }
    return selectedNames.length === 1
      ? selectedNames[0]
      : `${props.selectedPlaybookIds.length} selected`;
  })();

  const handleChange = (nextIds: readonly string[]) => {
    const previousIds = new Set(selectedIds);
    const addedLeadingOptionId = nextIds.find(
      (id) => !previousIds.has(id) && leadingOptionById.has(id),
    );
    if (addedLeadingOptionId !== undefined) {
      onChange({ playbookIds: [], addedLeadingOptionId });
      return;
    }

    const selected = new Set(nextIds);
    const playbookIds = props.options
      .filter((option) => selected.has(option.value))
      .map((option) => option.value);
    onChange({ playbookIds });
  };

  return (
    <MultiSelect
      label={selectionLabel}
      placeholder={props.isLoading ? "Loading options" : "Select options"}
      size={props.size}
      isDisabled={props.isLoading || props.isDisabled}
      sections={sections}
      value={selectedIds}
      onChange={handleChange}
    />
  );
};
